import { TextAlign } from '@myre/common-sheet';
import { type ActionGroup } from '@myre/ui-primitive-action-group';
import {
  MyuiComponent,
  MyuiTemplate,
  PaginationComponent,
} from '@myre/ui-primitive-components';
import {
  CellType,
  MultiTableComponent,
  type Cell,
  type IconCell,
  type RowCheckboxState,
  type Table,
} from '@myre/ui-primitive-multi-table';

import { NgTemplateOutlet } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { type MatSort, type Sort } from '@angular/material/sort';
import { compact } from 'lodash-es';

import {
  Theme,
  type Column,
  type PaginationList,
  type Row,
} from '../interfaces';

const ICON_KEY = 'internal-list_icon';
const DEFAULT_WIDTH = '40px';

/**
 * The list component displays a list of items with the possibility to add icon navigation.
 * The list is potentially paginated.
 */
@Component({
  selector: 'myre-list-paginated',
  templateUrl: './list-paginated.component.html',
  styleUrls: ['./list-paginated.component.scss'],
  host: {
    '[class.card]': 'theme() === _theme.StandaloneCard',
    '[class.component]': 'theme() === _theme.PartOfComponent',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MyuiTemplate,
    NgTemplateOutlet,
    MultiTableComponent,
    PaginationComponent,
  ],
})
export class ListPaginatedComponent extends MyuiComponent {
  public override mSelector = 'myre-list-paginated';

  /**
   * The columns parameters to display
   */
  public readonly columns = input.required<Column<string>[] | null>();

  /**
   * The rows to display in the list.
   * The keys of each Record must correspond to the key of the corresponding header.
   */
  public readonly rows = input.required<Row<string>[] | null>();

  /**
   * The pagination information to display on the list.
   * If null, the pagination is not displayed.
   */
  public readonly pagination = input<PaginationList>(null);

  /**
   * If true, the rows are cursor pointer and emit an event when clicked.
   */
  public readonly clickable = input<boolean>(true);

  /**
   * If true, display an arrow icon at the end of each row.
   */
  public readonly iconNavigation = input<boolean>(false);

  /**
   * If truthy, display a filter message when the list is empty.
   * If a string is provided, it is used as the filter message.
   * If false, the message is a simple "No data" message.
   */
  public readonly filterStatus = input<boolean | string>(false);

  public readonly actionsForSelectedRow = input<ActionGroup | null>(null);

  public readonly theme = input<Theme>(Theme.StandaloneCard);

  /**
   * If true, column widths are read as pixels and the list scrolls horizontally instead of squeezing its columns.
   */
  public readonly widthInPixel = input(false, { transform: booleanAttribute });

  /**
   * Override the default sorting function for sortable columns.
   * Use when cells aren't plain strings (numbers, dates, tags) and the
   * lexicographic default sort wouldn't be meaningful.
   */
  public readonly sortCallback = input<
    ((data: Table<string>[], sort: MatSort) => Table<string>[]) | undefined
  >(undefined);

  /**
   * The event emitted when a row is clicked and passed the id of the row.
   * This event is emitted only if `clickable` is true; rows with `isLoading` true never emit.
   */
  public readonly onRowClicked = output<string | number>();

  public readonly rowSelectionChange =
    output<Map<string | number, RowCheckboxState>>();

  public readonly onPagination = output<number>();

  public readonly sortChange = output<Sort>();

  protected readonly _theme = Theme;

  private readonly _loadingPagination = signal<{
    pagination: PaginationList;
    numberOfRows: number;
  } | null>(null);

  constructor() {
    super();
    // Reset loading state when new data arrives
    effect(() => {
      const currentRows = this.rows();

      if (currentRows) {
        this._loadingPagination.set(null);
      }
    });
  }

  public selectRows(ids: Array<string | number>): void {
    this._multiTableRef().selectRows(ids);
  }

  protected readonly _multiTableRef = viewChild<MultiTableComponent>('table');

  protected readonly _filterResultTemplate = computed(() => {
    return this._getTemplate('filterResult');
  });

  protected readonly _columns = computed<Column<string>[]>(() =>
    // Map the header columns to the format expected by the multi-table component
    // and add extra columns for icon navigation if needed
    compact([
      ...(this.columns() || []).map(c => ({
        ...c,
        align: c.align ?? TextAlign.Left,
        canSort: c.canSort ?? false,
      })),
      this.iconNavigation() ? this._createColumn(ICON_KEY) : null,
    ]),
  );

  protected readonly _table = computed<Table<string>>(() => {
    const currentRows = this.rows();
    const loadingPagination = this._loadingPagination();

    // If we have loading pagination, generate dummy rows
    if (loadingPagination !== null) {
      const dummyRows = Array.from(
        { length: loadingPagination.numberOfRows },
        (_, index): Row<string> => ({
          id: `loading-${index}`,
          checkboxState: undefined,
          onCheckboxClick: undefined,
          isLoading: true,
          onClick: undefined,
          cells:
            this.columns()?.reduce(
              (acc, header) => {
                acc[header.key] = { type: CellType.String, value: '' };

                return acc;
              },
              {} as unknown as Record<string, Cell>,
            ) || {},
        }),
      );

      return { rows: dummyRows };
    }

    if (!currentRows) {
      return { rows: null };
    }

    return {
      rows: currentRows.map(row => ({
        id: row.id,
        checkboxState: row.checkboxState,
        onCheckboxClick: row.onCheckboxClick,
        isLoading: row.isLoading || false,
        onClick:
          this.clickable() && !row.isLoading
            ? (event: Event) => {
                event.stopPropagation();
                row.onClick?.(event);
                this.onRowClicked.emit(row.id);
              }
            : undefined,
        cells: {
          ...row.cells,
          [ICON_KEY]: this.iconNavigation() ? this._getClickableCell() : null,
        },
      })),
    };
  });

  protected readonly _displayPagination = computed<PaginationList | null>(
    () => {
      const loadingPagination = this._loadingPagination();
      const currentPagination = this.pagination();

      // Use loading pagination if available, otherwise use current pagination
      return loadingPagination?.pagination || currentPagination;
    },
  );

  protected _handlePagination(page: number): void {
    // Preserve pagination state during loading with updated current page
    const currentPagination = this.pagination();
    const currentRowCount = this.rows()?.length || 10;

    if (currentPagination) {
      this._loadingPagination.set({
        pagination: {
          ...currentPagination,
          currentPage: page,
        },
        numberOfRows: currentRowCount,
      });
    }

    this.onPagination.emit(page);
  }

  private _createColumn(key: string): Column<string> {
    return {
      key,
      label: '',
      maxWidth: DEFAULT_WIDTH,
      align: TextAlign.Right,
      canSort: false,
    };
  }

  private _getClickableCell(): IconCell {
    return {
      type: CellType.Icon,
      icon: 'arrow_next',
      options: {
        color: 'var(--color-ui-greyblue-500)',
      },
    };
  }
}
